import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { listMockupsInFolder } from '../services/azureStorage';
import appsConfig from '../config.json';
import TechnologySection from '../components/TechnologySection';
import GallerySection from '../components/GallerySection';
import Lightbox from '../components/Lightbox';
import './AppDetail.css';

function AppDetail() {
  const { appName } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [mockups, setMockups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageOrientations, setImageOrientations] = useState({});
  const [heroImage, setHeroImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadAppDetails = async () => {
      const foundApp = appsConfig.apps.find(a => a.name === appName);
      if (!foundApp) {
        navigate('/');
        return;
      }

      setApp(foundApp);

      // Try card_up.png first, fallback to card.png
      const sasToken = process.env.REACT_APP_SAS_TOKEN ? '?' + process.env.REACT_APP_SAS_TOKEN : '';
      const cardUpUrl = `https://${process.env.REACT_APP_STORAGE_ACCOUNT}.blob.core.windows.net/${process.env.REACT_APP_CONTAINER_NAME}/${foundApp.name}/card_up.png${sasToken}`;
      const cardUrl = `https://${process.env.REACT_APP_STORAGE_ACCOUNT}.blob.core.windows.net/${process.env.REACT_APP_CONTAINER_NAME}/${foundApp.name}/card.png${sasToken}`;
      
      // Try loading card_up.png using Image object to avoid CORS issues with fetch
      const img = new Image();
      img.onload = () => setHeroImage(cardUpUrl);
      img.onerror = () => setHeroImage(cardUrl);
      img.src = cardUpUrl;

      try {
        const mockupUrls = await listMockupsInFolder(appName);
        setMockups(mockupUrls);
        
        // Detect image orientations
        const orientations = {};
        mockupUrls.forEach((url, index) => {
          const img = new Image();
          img.onload = () => {
            orientations[index] = img.width > img.height ? 'landscape' : 'portrait';
            setImageOrientations({...orientations});
          };
          img.src = url;
        });
      } catch (error) {
        console.error('Error loading mockups (CORS may need to be configured):', error);
        // Set empty array to allow page to load without gallery
        setMockups([]);
      }

      setLoading(false);
    };

    loadAppDetails();
  }, [appName, navigate]);

  if (loading || !app || !heroImage) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="app-detail">
      {/* Hero Section */}
      <div 
        className="hero-section"
        style={{ backgroundImage: `url('${heroImage}')` }}
      >
        <div className="hero-overlay"></div>
        <button className="back-button" onClick={() => navigate('/')}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
      </div>

      {/* App Info Section */}
      <div className="app-info-section">
        <div className="app-info-container">
          <div className="app-description-wrapper">
            <p className="app-description">{app.short}</p>
            {app.functionalDoc && (
              <a
                href={`https://${process.env.REACT_APP_STORAGE_ACCOUNT}.blob.core.windows.net/${process.env.REACT_APP_CONTAINER_NAME}/${app.name}/functional.pdf${process.env.REACT_APP_SAS_TOKEN ? '?' + process.env.REACT_APP_SAS_TOKEN : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="functional-doc-link"
              >
                View the detailed functional document →
              </a>
            )}
          </div>
          <h1 className="app-title">How the App Works</h1>
        </div>
      </div>

      {/* Technology Section */}
      <TechnologySection technologies={app.technologies} />

      {/* Gallery Section */}
      <GallerySection
        mockups={mockups}
        imageOrientations={imageOrientations}
        onImageClick={(index) => setSelectedImageIndex(index)}
        appName={app.name}
      />

      {/* Lightbox */}
      <Lightbox
        images={mockups}
        currentIndex={selectedImageIndex}
        isZoomed={isZoomed}
        onClose={() => { setSelectedImageIndex(null); setIsZoomed(false); }}
        onPrevious={() => { setSelectedImageIndex(selectedImageIndex - 1); setIsZoomed(false); }}
        onNext={() => { setSelectedImageIndex(selectedImageIndex + 1); setIsZoomed(false); }}
        onToggleZoom={() => setIsZoomed(!isZoomed)}
      />
    </div>
  );
}

export default AppDetail;
